import { alpha } from '@mui/material/styles';

/** Hero typography on full-page gradients — dark upper bands need light text. */
export function getPageHeroTextColors(templateId, theme) {
  if (['medium', 'dark', 'aqua', 'orchid'].includes(templateId)) {
    return { primary: '#f8fafc', secondary: '#cbd5e1' };
  }
  return { primary: theme.textPrimary, secondary: theme.textSecondary };
}

/** Outlined button styles that always contrast with a panel/card theme slice. */
export function panelOutlinedButtonSx(theme) {
  return {
    textTransform: 'none',
    color: theme.textPrimary,
    borderColor: theme.panelBorder,
    '&:hover': {
      borderColor: theme.textPrimary,
      bgcolor: alpha(theme.textPrimary, 0.08)
    },
    '& .MuiButton-startIcon': { color: theme.textSecondary }
  };
}

/** Soft pastel fills for outlined nav boxes (dark outline stays #0f172a). */
export const MENU_BOX_COLORS = {
  home: { bg: '#E0F2FE', hover: '#BAE6FD' },
  signin: { bg: '#FEF3C7', hover: '#FDE68A' },
  register: { bg: '#DCFCE7', hover: '#BBF7D0' },
  browsing: { bg: '#F3E8FF', hover: '#E9D5FF' },
  dashboard: { bg: '#E0E7FF', hover: '#C7D2FE' },
  logout: { bg: '#FEE2E2', hover: '#FECACA' },
  jobs: { bg: '#DBEAFE', hover: '#BFDBFE' },
  trainings: { bg: '#FCE7F3', hover: '#FBCFE8' },
  exams: { bg: '#FEF9C3', hover: '#FEF08A' },
  benches: { bg: '#CCFBF1', hover: '#99F6E4' }
};

/**
 * Shared box size matching Sign In / Login / Register / Signup
 * so Jobs, Trainings, Exams, Benches, and access actions align.
 * Labels are optically centered (no start-icon offset).
 */
export const MENU_NAV_BOX_SIZE_SX = {
  width: 176,
  minWidth: 176,
  maxWidth: 176,
  height: 40,
  minHeight: 40,
  boxSizing: 'border-box',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  px: 1,
  py: 0,
  fontSize: '0.8125rem',
  lineHeight: 1.2,
  letterSpacing: 0.01
};

/** Bright “you are here” sparkle for the active public nav box. */
export const MENU_NAV_ACTIVE_SPARKLE_SX = {
  fontWeight: 800,
  color: '#0f172a',
  bgcolor: '#FDE047',
  border: '2px solid #CA8A04',
  backgroundImage:
    'linear-gradient(110deg, #FDE047 0%, #FEF08A 35%, #FFFFFF 50%, #FEF08A 65%, #FACC15 100%)',
  backgroundSize: '220% 100%',
  boxShadow:
    '0 0 0 1px rgba(250, 204, 21, 0.55), 0 0 14px rgba(250, 204, 21, 0.85), 0 2px 8px rgba(15, 23, 42, 0.12)',
  animation: 'erp4cNavSparkle 1.75s ease-in-out infinite',
  '@keyframes erp4cNavSparkle': {
    '0%, 100%': {
      backgroundPosition: '0% 50%',
      boxShadow:
        '0 0 0 1px rgba(250, 204, 21, 0.45), 0 0 10px rgba(250, 204, 21, 0.65), 0 2px 8px rgba(15, 23, 42, 0.1)'
    },
    '50%': {
      backgroundPosition: '100% 50%',
      boxShadow:
        '0 0 0 2px rgba(234, 179, 8, 0.7), 0 0 20px rgba(253, 224, 71, 0.95), 0 2px 10px rgba(15, 23, 42, 0.14)'
    }
  },
  '&:hover': {
    bgcolor: '#FACC15',
    border: '2px solid #A16207',
    backgroundImage:
      'linear-gradient(110deg, #FDE047 0%, #FEF08A 35%, #FFFFFF 50%, #FEF08A 65%, #FACC15 100%)',
    boxShadow:
      '0 0 0 2px rgba(234, 179, 8, 0.75), 0 0 18px rgba(250, 204, 21, 0.9), 0 2px 10px rgba(15, 23, 42, 0.14)'
  }
};

/**
 * Light fill + dark black outline for public/app menu actions
 * (Home, Sign In, Register, Browsing, Dashboard, Logout).
 * Pass `tone` from MENU_BOX_COLORS keys, or explicit `bg` / `hoverBg`.
 * Set `uniform: true` for matching width & height; `active` adds bright sparkle.
 */
export function menuOutlinedBoxButtonSx({ active = false, tone, bg, hoverBg, uniform = false } = {}) {
  const palette = tone && MENU_BOX_COLORS[tone] ? MENU_BOX_COLORS[tone] : null;
  const fill = bg || palette?.bg || '#f8fafc';
  const hover = hoverBg || palette?.hover || '#e2e8f0';
  return {
    textTransform: 'none',
    fontWeight: active ? 800 : 600,
    color: '#0f172a',
    bgcolor: fill,
    border: '1.5px solid #0f172a',
    borderRadius: 1,
    boxShadow: 'none',
    px: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    ...(uniform ? MENU_NAV_BOX_SIZE_SX : {}),
    '&:hover': {
      bgcolor: hover,
      border: '1.5px solid #000000',
      boxShadow: 'none'
    },
    ...(active ? MENU_NAV_ACTIVE_SPARKLE_SX : {})
  };
}
